import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, FileText, Upload, X, Plus, Trash2, Copy } from "lucide-react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  getServiceScript,
  updateServiceScript,
  getServiceScriptTags,
  storeServiceScriptAttachment,
  deleteServiceScriptAttachment,
} from "@/Api/serviceScripts";
import { getSelectContests } from "@/Api/contests";
import { ServiceScript, Tag, ServiceScriptAttachment, NewTag } from "@/types/serviceScript";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

type SelectedTag = number | NewTag;

const TAG_COLORS = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16",
  "#22c55e", "#10b981", "#14b8a6", "#06b6d4", "#0ea5e9",
  "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7", "#d946ef",
  "#ec4899", "#f43f5e", "#6b7280", "#78716c", "#0f172a",
];

const serviceScriptSchema = z.object({
  title: z.string().min(1, "Título é obrigatório").max(255),
  content: z.string().min(1, "Conteúdo é obrigatório"),
  contest_id: z.number().nullable().optional(),
  is_active: z.boolean().default(true),
});

type ServiceScriptForm = z.infer<typeof serviceScriptSchema>;

interface Lottery {
  id: number;
  name: string;
  color: string;
}

interface Contest {
  id: number;
  uuid: string;
  name: string;
  lottery?: Lottery;
}

interface UpdateServiceScriptProps {
  uuid: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => Promise<void>;
}

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "color",
  "background",
  "link",
];

export function UpdateServiceScript({
  uuid,
  isOpen,
  onClose,
  onUpdate,
}: UpdateServiceScriptProps) {
  const [contests, setContests] = useState<Contest[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<SelectedTag[]>([]);
  const [newTagInput, setNewTagInput] = useState("");
  const [newTagColor, setNewTagColor] = useState("#3b82f6");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [existingAttachments, setExistingAttachments] = useState<ServiceScriptAttachment[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [editorContent, setEditorContent] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ServiceScriptForm>({
    resolver: zodResolver(serviceScriptSchema),
    defaultValues: {
      title: "",
      content: "",
      contest_id: null,
      is_active: true,
    },
  });

  const isActive = watch("is_active");

  useEffect(() => {
    const loadData = async () => {
      if (!isOpen || !uuid) return;

      setIsLoadingData(true);
      try {
        const [scriptRes, contestsRes, tagsRes] = await Promise.all([
          getServiceScript(uuid),
          getSelectContests(),
          getServiceScriptTags(),
        ]);

        if (contestsRes.data?.response) {
          setContests(contestsRes.data.response);
        }
        if (tagsRes.data?.response) {
          setTags(tagsRes.data.response);
        }

        if (scriptRes.data?.response) {
          const script: ServiceScript = scriptRes.data.response;
          setValue("title", script.title);
          setValue("content", script.content);
          setValue("contest_id", script.contest_id);
          setValue("is_active", script.is_active);
          setEditorContent(script.content);
          setSelectedTags(script.tags?.map((t) => t.id) || []);
          setExistingAttachments(script.attachments || []);
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        toast.error("Erro ao carregar script");
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [isOpen, uuid, setValue]);

  const handleEditorChange = (content: string) => {
    setEditorContent(content);
    setValue("content", content, { shouldValidate: true });
  };

  const handleCopyContent = async () => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = editorContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    try {
      await navigator.clipboard.writeText(textContent);
      toast.success("Conteúdo copiado!");
    } catch (error) {
      toast.error("Erro ao copiar conteúdo");
    }
  };

  const handleTagSelect = (tagId: string) => {
    const numId = parseInt(tagId);
    const alreadySelected = selectedTags.some(
      (t) => typeof t === "number" && t === numId
    );
    if (!alreadySelected) {
      setSelectedTags([...selectedTags, numId]);
    }
  };

  const handleAddNewTag = () => {
    if (newTagInput.trim()) {
      const alreadySelected = selectedTags.some(
        (t) => typeof t === "object" && t.name === newTagInput.trim()
      );
      if (!alreadySelected) {
        setSelectedTags([...selectedTags, { name: newTagInput.trim(), color: newTagColor }]);
        setNewTagInput("");
        setNewTagColor("#3b82f6");
      }
    }
  };

  const handleRemoveTag = (tag: SelectedTag) => {
    setSelectedTags(selectedTags.filter((t) => {
      if (typeof t === "number" && typeof tag === "number") {
        return t !== tag;
      }
      if (typeof t === "object" && typeof tag === "object") {
        return t.name !== tag.name;
      }
      return true;
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setNewFiles([...newFiles, ...Array.from(e.target.files)]);
    }
  };

  const handleRemoveNewFile = (index: number) => {
    setNewFiles(newFiles.filter((_, i) => i !== index));
  };

  const handleDeleteExistingAttachment = async (attachmentId: number) => {
    try {
      const response = await deleteServiceScriptAttachment(uuid, attachmentId);
      if (response.status === 200) {
        setExistingAttachments(
          existingAttachments.filter((a) => a.id !== attachmentId)
        );
        toast.success("Arquivo removido com sucesso!");
      } else {
        toast.error("Erro ao remover arquivo");
      }
    } catch (error) {
      toast.error("Erro ao remover arquivo");
    }
  };

  const onSubmit = async (data: ServiceScriptForm) => {
    setIsLoading(true);
    try {
      const response = await updateServiceScript(uuid, {
        ...data,
        tags: selectedTags,
      });

      if (response.status === 200) {
        if (newFiles.length > 0) {
          for (const file of newFiles) {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("name", file.name);
            await storeServiceScriptAttachment(uuid, formData);
          }
        }

        toast.success("Script atualizado com sucesso!");
        setNewFiles([]);
        onClose();

        if (onUpdate) {
          await onUpdate();
        }
      } else {
        toast.error(response.data?.message || "Erro ao atualizar script");
      }
    } catch (error) {
      toast.error("Erro ao atualizar script");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      reset();
      setSelectedTags([]);
      setNewFiles([]);
      setNewTagInput("");
      setNewTagColor("#3b82f6");
      setEditorContent("");
      onClose();
    }
  };

  const getTagName = (tag: SelectedTag): string => {
    if (typeof tag === "object") return tag.name;
    const foundTag = tags.find((t) => t.id === tag);
    return foundTag?.name || String(tag);
  };

  const getTagColor = (tag: SelectedTag): string => {
    if (typeof tag === "object") return tag.color;
    const foundTag = tags.find((t) => t.id === tag);
    return foundTag?.color || "#6b7280";
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            Editar Script de Atendimento
          </DialogTitle>
          <DialogDescription>
            Edite as informações do script de atendimento.
          </DialogDescription>
        </DialogHeader>

        {isLoadingData ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                {...register("title")}
                placeholder="Digite o título do script"
              />
              {errors.title && (
                <p className="text-sm text-red-500">{errors.title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="content">Conteúdo *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyContent}
                  disabled={!editorContent}
                  className="h-7 gap-1"
                >
                  <Copy className="h-3 w-3" />
                  Copiar
                </Button>
              </div>
              <div className="border rounded-md [&_.ql-container]:min-h-[250px] [&_.ql-editor]:min-h-[250px]">
                <ReactQuill
                  theme="snow"
                  value={editorContent}
                  onChange={handleEditorChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Digite o texto do script que será copiado pelos vendedores..."
                />
              </div>
              {errors.content && (
                <p className="text-sm text-red-500">{errors.content.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Concurso</Label>
              <Select
                value={watch("contest_id")?.toString() || "atemporal"}
                onValueChange={(value) =>
                  setValue(
                    "contest_id",
                    value === "atemporal" ? null : parseInt(value)
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um concurso (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="atemporal">Atemporal</SelectItem>
                  {contests.map((contest) => (
                    <SelectItem key={contest.id} value={contest.id.toString()}>
                      <div className="flex items-center gap-2">
                        <span>{contest.name}</span>
                        {contest.lottery && (
                          <span
                            className="px-2 py-0.5 text-xs rounded-full text-white"
                            style={{ backgroundColor: contest.lottery.color }}
                          >
                            {contest.lottery.name}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Select onValueChange={handleTagSelect}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecione uma tag" />
                  </SelectTrigger>
                  <SelectContent>
                    {tags
                      .filter((t) => !selectedTags.some((st) => typeof st === "number" && st === t.id))
                      .map((tag) => (
                        <SelectItem key={tag.id} value={tag.id.toString()}>
                          <div className="flex items-center gap-2">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 items-center">
                <Input
                  placeholder="Nova tag"
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && (e.preventDefault(), handleAddNewTag())
                  }
                  className="flex-1"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="color"
                    value={newTagColor}
                    onChange={(e) => setNewTagColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border-0"
                    title="Cor da tag"
                  />
                  <div className="flex flex-wrap gap-1">
                    {TAG_COLORS.slice(0, 10).map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`w-5 h-5 rounded-full border-2 ${newTagColor === color ? 'border-gray-900 dark:border-white' : 'border-transparent'}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setNewTagColor(color)}
                      />
                    ))}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddNewTag}
                  disabled={!newTagInput.trim()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              {selectedTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 text-xs rounded-full text-white cursor-pointer hover:opacity-80"
                      style={{ backgroundColor: getTagColor(tag) }}
                      onClick={() => handleRemoveTag(tag)}
                    >
                      {getTagName(tag)}
                      <X className="h-3 w-3 ml-1" />
                    </span>
                  ))}
                </div>
              )}
            </div>

            {existingAttachments.length > 0 && (
              <div className="space-y-2">
                <Label>Arquivos existentes</Label>
                <div className="space-y-2">
                  {existingAttachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-2 bg-muted rounded"
                    >
                      <a
                        href={attachment.file_path || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm truncate text-primary hover:underline"
                      >
                        {attachment.name}
                      </a>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteExistingAttachment(attachment.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Adicionar arquivos</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                <input
                  type="file"
                  id="file-upload-update"
                  className="hidden"
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.mp4,.mov,.avi,.mkv,.webm"
                  onChange={handleFileChange}
                />
                <label
                  htmlFor="file-upload-update"
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">
                    Clique para adicionar arquivos
                  </span>
                  <span className="text-xs text-muted-foreground mt-1">
                    JPG, PNG, PDF, MP4, MOV (max 100MB)
                  </span>
                </label>
              </div>
              {newFiles.length > 0 && (
                <div className="space-y-2 mt-2">
                  {newFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-muted rounded"
                    >
                      <span className="text-sm truncate">{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveNewFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Ativo</Label>
              <Switch
                id="is_active"
                checked={isActive}
                onCheckedChange={(checked) => setValue("is_active", checked)}
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading} className="gap-2">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
